/**
 * Public Navbar for landing pages - Black & Gold theme with dark mode support
 */
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';

export function PublicNavbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { firebaseUser, signOut } = useAuth();

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/public-services', label: t('nav.services') },
    { path: '/contact', label: t('nav.contact') },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-canvas/95 backdrop-blur-lg border-b border-hairline transition-colors duration-300 pt-7 md:pt-0" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 28px)' }}>
      <div className="mx-auto max-w-5xl px-4 md:px-6">
        {/* Mobile Layout - Clean and minimal */}
        <div className="md:hidden flex items-center justify-between h-10">
          {/* Logo - LEFT */}
          <Link to="/" className="flex items-center gap-1.5 group">
            <img
              src='/logo/logo_monkey.svg'
              alt="Monkey Barber Logo"
              className="h-7 w-auto"
            />
            <div className="text-xs font-black text-accent font-zarathos">
              MONKEY
            </div>
          </Link>

          {/* Right Section - Mobile: Only essential buttons */}
          <div className="flex items-center gap-1.5">
            {/* Main CTA Button */}
            {firebaseUser ? (
              <Link to="/dashboard">
                <button className="bg-accent text-on-accent font-bold px-2.5 py-1 rounded-md uppercase tracking-wide text-[0.625rem]">
                  Dashboard
                </button>
              </Link>
            ) : (
              <Link to="/take-ticket">
                <button className="bg-accent text-on-accent font-bold px-2.5 py-1 rounded-md uppercase tracking-wide text-[0.625rem]">
                  Reservar
                </button>
              </Link>
            )}

            {/* Hamburger Menu */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center text-ink-muted hover:text-accent transition-colors"
              aria-label="Toggle menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Desktop Layout - flex justify-between */}
        <div className="hidden md:flex items-center justify-between h-16 gap-4">
          {/* Logo - positioned on the left */}
          <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
            <img
              src='/logo/logo_monkey.svg'
              alt="Monkey Barber Logo"
              className="h-10 lg:h-12 w-auto transition-transform duration-300 group-hover:scale-110"
            />
            <div>
              <div className="text-xl lg:text-2xl font-black text-accent font-zarathos">
                MONKEY
              </div>
              <div className="text-xs text-ink-subtle uppercase tracking-wider -mt-1 hidden lg:block">Barber Club</div>
            </div>
          </Link>

          {/* Desktop Navigation - Hidden on md, shown on lg */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`
                  text-xs xl:text-sm font-medium uppercase tracking-wide transition-all whitespace-nowrap
                  ${isActive(link.path)
                    ? 'text-accent border-b-2 border-accent pb-1'
                    : 'text-ink-muted hover:text-accent'
                  }
                `}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Section - Desktop */}
          <div className="hidden md:flex items-center gap-2 lg:gap-4 flex-shrink-0">
            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg border border-hairline hover:border-accent text-ink-muted hover:text-accent transition-all"
              aria-label="Toggle theme"
              title={theme === 'dark' ? t('common.lightMode') : t('common.darkMode')}
            >
              {theme === 'dark' ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
            <LanguageSwitcher />

            {/* Show different buttons based on auth status */}
            {firebaseUser ? (
              // Authenticated user buttons
              <>
                <Link to="/dashboard">
                  <button className="bg-accent hover:bg-accent-hover text-on-accent font-bold px-3 lg:px-6 py-2 lg:py-2.5 rounded-lg shadow-lg transition-all uppercase tracking-wide text-xs lg:text-sm">
                    {t('nav.dashboard')}
                  </button>
                </Link>
                <button
                  onClick={handleLogout}
                  className="hidden lg:block text-danger hover:bg-danger/20 border border-danger/40 px-6 py-2.5 rounded-lg transition-all uppercase tracking-wide text-sm font-medium"
                >
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              // Guest user buttons
              <>
                <Link to="/take-ticket">
                  <button className="bg-accent hover:bg-accent-hover text-on-accent font-bold px-3 lg:px-6 py-2 lg:py-2.5 rounded-lg shadow-lg transition-all uppercase tracking-wide text-xs lg:text-sm">
                    {t('nav.bookTicket')}
                  </button>
                </Link>
                <Link to="/login" className="hidden lg:block">
                  <button className="text-ink-muted hover:text-accent border border-hairline hover:border-accent px-6 py-2.5 rounded-lg transition-all uppercase tracking-wide text-sm font-medium">
                    {t('nav.login')}
                  </button>
                </Link>
              </>
            )}

            {/* Hamburger for md screens (when nav links are hidden) */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center text-ink-muted hover:text-accent transition-colors"
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile/Tablet Menu - Full navigation + settings */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-surface border-t border-hairline transition-colors duration-300">
          <div className="mx-auto max-w-5xl px-6 py-4">
            {/* Navigation Links */}
            <div className="space-y-1 mb-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`
                    block py-3 text-sm font-medium uppercase tracking-wide transition-colors
                    ${isActive(link.path)
                      ? 'text-accent border-l-4 border-accent pl-3'
                      : 'text-ink-muted hover:text-accent pl-3'
                    }
                  `}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Divider */}
            <div className="border-t border-hairline my-4"></div>

            {/* Settings Row: Theme + Language */}
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-ink-subtle">Configuración</span>
              <div className="flex items-center gap-3">
                {/* Theme Toggle */}
                <button
                  onClick={toggleTheme}
                  className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg border border-hairline text-ink-muted"
                  aria-label="Toggle theme"
                >
                  {theme === 'dark' ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                  )}
                </button>
                {/* Language */}
                <LanguageSwitcher />
              </div>
            </div>

            {/* Auth Button */}
            {firebaseUser ? (
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}
                className="w-full py-3 text-center text-danger border border-danger/40 hover:bg-danger/20 rounded-lg font-medium uppercase tracking-wide text-sm transition-colors"
              >
                {t('nav.logout')}
              </button>
            ) : (
              <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block">
                <button className="w-full py-3 text-center text-ink border border-hairline hover:bg-surface-2 rounded-lg font-medium uppercase tracking-wide text-sm transition-colors">
                  {t('nav.login')}
                </button>
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
